/**
 * Progress Service
 *
 * Handles user progress tracking and store synchronization.
 * Extracted from supabase.ts ProgressService class.
 *
 * @see PHASE4B_ARCHITECTURE.md for service specifications
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';
import type { AuthStoreAdapter, LearningStoreAdapter } from '@/services/adapters/store.adapters';
import { progressQueries } from '@/services/queries/progress.queries';
import { getCurrentLanguage } from '@/stores/useLanguageStore';

type UserProgressUpdate = Partial<Database['public']['Tables']['user_progress']['Update']>;

export class ProgressService {
  constructor(
    private supabase: SupabaseClient,
    private authStore: AuthStoreAdapter,
    private learningStore: LearningStoreAdapter
  ) {}

  /**
   * Fetch user progress from database and sync to store
   */
  async fetchUserProgress(language: string = 'es') {
    try {
      const user = this.authStore.getUser();
      if (!user) throw new Error('No user logged in');

      this.learningStore.setSyncStatus('syncing');

      const { data, error } = await progressQueries.getByUserAndLanguage(user.id, language);

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error;
      }

      if (data) {
        const progressData = {
          totalWordsKnown: data.total_words_known,
          totalConversations: data.total_conversations,
          totalLessonsCompleted: data.total_lessons_completed,
          currentStreak: data.current_streak,
          longestStreak: data.longest_streak,
          weeklyGoal: data.weekly_goal,
          weeklyProgress: data.weekly_progress,
          cefr_level: data.cefr_level as any,
        };

        this.learningStore.setProgress(progressData);
      }

      this.learningStore.markSynced();
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user progress:', error);
      this.learningStore.setSyncStatus('error');
      return { data: null, error };
    }
  }

  /**
   * Update user progress in database and sync to store
   * @param updates - Partial progress updates to apply
   * @param language - Optional language code, defaults to current target language
   */
  async updateUserProgress(updates: UserProgressUpdate, language?: string) {
    try {
      const user = this.authStore.getUser();
      if (!user) throw new Error('No user logged in');

      // Get current language from language store if not provided
      const targetLanguage = language || getCurrentLanguage();

      this.learningStore.setSyncStatus('syncing');

      const { data, error } = await progressQueries.upsertProgress(user.id, targetLanguage, updates);

      if (error) throw error;

      this.learningStore.markSynced();
      return { data, error: null };
    } catch (error) {
      console.error('Error updating user progress:', error);
      this.learningStore.setSyncStatus('error');
      return { data: null, error };
    }
  }
}
